// Keeps fetch() Resource Timing entries that overflow the page's capped buffer (default 250) for H5 attribution.

export const RETAINED_RESOURCE_TIMING_CAPACITY = 100;

let observer: PerformanceObserver | null = null;
let retained: PerformanceResourceTiming[] = [];

function retain(entries: PerformanceEntryList): void {
  for (const entry of entries) {
    // Glyph Map coverage and font bytes both use fetch().
    if ((entry as PerformanceResourceTiming).initiatorType === 'fetch') {
      retained.push(entry as PerformanceResourceTiming);
    }
  }
  if (retained.length > RETAINED_RESOURCE_TIMING_CAPACITY) {
    retained = retained.slice(-RETAINED_RESOURCE_TIMING_CAPACITY);
  }
}

/** Start before the buffer fills: `buffered` replays only entries the buffer still holds. */
export function startResourceTimingRetention(): void {
  if (observer || typeof PerformanceObserver === 'undefined') {
    return;
  }
  try {
    const current = new PerformanceObserver(list => retain(list.getEntries()));
    current.observe({ type: 'resource', buffered: true });
    observer = current;
  } catch {
    // Without typed resource observation, lookups use the buffer alone.
  }
}

/**
 * @param names - Absolute or relative URLs that identify the request.
 * @returns Retained entries whose URL matches one of `names`, in recording order.
 */
export function retainedResourceTimingEntries(names: readonly string[]): PerformanceResourceTiming[] {
  if (!observer) {
    return [];
  }
  // Observer callbacks are asynchronous, so drain entries recorded since the last one.
  retain(observer.takeRecords());
  return retained.filter(entry => names.includes(entry.name));
}

/** Disconnects the observer and clears retained entries between unit tests. */
export function resetResourceTimingRetentionForTests(): void {
  observer?.disconnect();
  observer = null;
  retained = [];
}
